export interface SpartaTrackReconciliationPreviewRequest {
  fileName: string;
  fileContentBase64: string;
  includeInactiveDispatches?: boolean;
  /** Multi-job endpoint only: resolve job groups (states, counts, hours) without building
   * per-group row previews — powers the instant "Jobs in this file" panel on file selection. */
  summaryOnly?: boolean;
  /** Single-job preview only: reconcile only rows whose file job number is listed
   * (empty string = blank job number). Used for selected "Not found in Sparta" groups. */
  includeFileJobNumbers?: string[] | null;
}

export interface SpartaTrackReconciliationPreview {
  totalRows: number;
  matchedRows: number;
  rowsWithDifferences: number;
  rows: SpartaTrackReconciliationRow[];
  employeeOptions: SpartaTrackReconciliationEmployeeOption[];
  workOrderOptions: SpartaTrackReconciliationWorkOrderOption[];
  classOptions: SpartaTrackReconciliationClassOption[];
  shiftOptions: number[];

  /** Per-tier rate-verification tallies (§10.7). Every performed tier comparison lands in
   * exactly one bucket, so match + mismatch + unverifiable = total comparisons. */
  rateMatchCount?: number;
  rateMismatchCount?: number;
  rateUnverifiableCount?: number;
}

/** Per-tier rate-verification state: the Track rate vs the dispatch's LISTED billing rate. */
export type SpartaTrackReconciliationRateVerifyStatus =
  | 'MATCH'
  | 'MISMATCH'
  | 'UNVERIFIABLE';

export interface SpartaTrackReconciliationEmployeeOption {
  employeeJobID: string;
  displayName: string;
  pid: string;
  craft: string;
  class: string;
  shift?: number;
}

export interface SpartaTrackReconciliationWorkOrderOption {
  workOrder: string;
  description: string;
  isBillable: boolean;
}

/** A work order already saved on existing Sparta labor for a row's employee+date, with its summed hours. */
export interface SpartaTrackReconciliationExistingWorkOrder {
  workOrder: string;
  hours: number;
}

export interface SpartaTrackReconciliationClassOption {
  craft: string;
  className: string;
}

/**
 * Apply writes HOURS ONLY. Rate and craft/class differences are identified in
 * the preview and resolved via the dispatch-update or step-up modals.
 */
export interface SpartaTrackReconciliationApplyRequest {
  clearMissingRateHours?: boolean;
  rows: SpartaTrackReconciliationRow[];
}

export interface SpartaTrackReconciliationApplyResult {
  attemptedRows: number;
  appliedRows: number;
  failedRows: number;
  rowResults: SpartaTrackReconciliationApplyRowResult[];
}

export interface SpartaTrackReconciliationApplyRowResult {
  rowNumber: number;
  applied: boolean;
  message: string;
}

export interface SpartaTrackReconciliationRow {
  selected: boolean;
  rowNumber: number;
  matchStatus: string;
  matchMessage: string;
  canApply: boolean;
  hasDifferences: boolean;
  hasHourDifferences: boolean;
  hasRateDifferences: boolean;
  hasCraftClassDifference: boolean;

  timesheetReference: string;
  dateWorked: string | null;
  pid: string;
  firstName: string;
  lastName: string;

  skillType: string;
  groupName: string;
  areaID: string;
  level2: string;
  shift?: number;

  rate1Hours?: number;
  rate1Rate?: number;
  rate2Hours?: number;
  rate2Rate?: number;
  rate3Hours?: number;
  rate3Rate?: number;

  /** The dispatch's LISTED per-tier billing rate (LaborBillingTemplate lineage); null when
   * unresolvable or hidden from the caller. */
  listedStRate?: number | null;
  listedOtRate?: number | null;
  listedPtRate?: number | null;

  /** Per-tier verification of the Track rate against the listed rate; null when the tier has
   * no Track activity (no hours and no rate), so no comparison was performed. */
  rateVerifyStatusSt?: SpartaTrackReconciliationRateVerifyStatus | null;
  rateVerifyStatusOt?: SpartaTrackReconciliationRateVerifyStatus | null;
  rateVerifyStatusPt?: SpartaTrackReconciliationRateVerifyStatus | null;

  /** Sheet-2 row: participates in rate verification only, never hours, never appliable (§10.8). */
  rateVerificationOnly?: boolean;

  employeeJobID?: string;
  employeeDisplayName: string;
  currentCraft: string;
  currentClass: string;
  targetCraft: string;
  targetClass: string;
  workOrder: string;
  isBillableWorkOrder?: boolean;

  /** Work orders already saved on this employee+date's existing Sparta labor (distinct
   * phases with summed hours; zero-hour lines excluded). Empty when no labor exists yet. */
  existingWorkOrders?: SpartaTrackReconciliationExistingWorkOrder[];

  isPayrollLocked: boolean;
  payrollLockMessage: string;

  note: string;

  existingStEmployeeLaborID?: string;
  existingOtEmployeeLaborID?: string;
  existingPtEmployeeLaborID?: string;
  lineGroupID?: string;

  stEarnCode: string;
  otEarnCode: string;
  ptEarnCode: string;

  existingStHours?: number;
  existingOtHours?: number;
  existingPtHours?: number;
  existingStBillRate?: number;
  existingOtBillRate?: number;
  existingPtBillRate?: number;

  deltaStHours?: number;
  deltaOtHours?: number;
  deltaPtHours?: number;
  deltaTotalHours?: number;

  /** Work order came from the alphabetical-first-phase fallback; confirm before applying (§10.3). */
  workOrderGuessed?: boolean;

  /** Same person+date summed across all job groups in the file exceeds the daily threshold (§10.6). */
  crossJobDailyTotalFlag?: boolean;
  crossJobDailyTotalHours?: number | null;
}

export type SpartaTrackReconciliationFlavor = 'standard' | 'exxon';

export type SpartaTrackReconciliationJobGroupState =
  | 'resolved'
  | 'unresolved'
  | 'no-permission';

/**
 * Multi-job (Exxon-flavor) preview: the file drives the scope. Standard-flavor
 * files come back with flavor 'standard' and no groups so the page falls back
 * to the single-job flow (design: track-reconciliation-multi-job.md §2, §4).
 */
export interface SpartaTrackReconciliationMultiJobPreview {
  flavor: SpartaTrackReconciliationFlavor;
  jobGroups: SpartaTrackReconciliationJobGroup[];
  totalFileRows: number;
  laborRowCount: number;
  equipmentRowCount: number;
  materialsRowCount: number;
  undatedRowCount: number;

  /** Which column mapping parsed the file (DB mapping or built-in flavor); null when needsMapping. */
  mappingUsed?: SpartaTrackReconciliationMappingUsed | null;
  /** No mapping fits and no built-in flavor matches — fileHeaders carries the header
   * row so the client opens the mapping editor instead of failing. */
  needsMapping?: boolean;
  fileHeaders?: string[];
}

export interface SpartaTrackReconciliationMappingUsed {
  trackColumnMappingID?: string | null;
  name: string;
  scope: 'job' | 'facility' | 'generic' | 'built-in';
  /** Built-in mappings only: target field → source column header. */
  mapping?: Record<string, string> | null;
}

export interface SpartaTrackColumnField {
  field: string;
  label: string;
  required: boolean;
  /** Fields sharing a group satisfy identity together: 'identity' (pid alone) or
   * 'identity-name' (first + last name together). */
  requiredGroup?: string | null;
  hint?: string | null;
}

export interface SpartaTrackColumnMapping {
  trackColumnMappingID: string;
  name: string;
  jobID?: string | null;
  jobName?: string | null;
  parentFacilityID?: number | null;
  parentFacilityName?: string | null;
  /** Target field key → source column header text. */
  mapping: Record<string, string>;
  bActive: boolean;
  /** Security user id (GUID) of the last editor. */
  modifiedBy: string;
  modifiedDate?: string | null;
}

export interface SpartaTrackColumnMappingCatalog {
  fields: SpartaTrackColumnField[];
  mappings: SpartaTrackColumnMapping[];
}

export interface SpartaTrackReconciliationJobGroup {
  vpJobNumber: string;
  jobID?: string | null;
  jobName?: string | null;
  state: SpartaTrackReconciliationJobGroupState;
  canApply: boolean;
  rowCount: number;
  totalHours: number;
  workOrderGuessedCount: number;
  equipmentRowCount: number;
  materialsRowCount: number;
  /** Full preview for resolved groups; file-only rows for no-permission groups; null when unresolved. */
  preview?: SpartaTrackReconciliationPreview | null;
}
